import React, { useMemo } from 'react';
import { ExternalLink } from 'lucide-react';

export interface ComponentUsageInfo {
    buildingKey: string;
    buildingName: string;
    componentKey: string;
    componentName: string;
    levelDisplay: string | null;
    roomDisplay: string | null;
}

export interface GroupedByBuilding {
    buildingKey: string;
    buildingName: string;
    levels: {
        levelDisplay: string | null;
        rooms: {
            roomDisplay: string | null;
            components: ComponentUsageInfo[];
        }[];
    }[];
}

const compareNullableFirst = (a: string | null, b: string | null): number => {
    if (!a && b) return -1;
    if (a && !b) return 1;
    if (!a && !b) return 0;
    return a!.localeCompare(b!, 'sv');
};

/**
 * Groups component instances by building > level > room, each level sorted
 * by Swedish name with the building-wide / room-less groups first.
 */
export const groupUsageByBuilding = (usage: ComponentUsageInfo[]): GroupedByBuilding[] => {
    const buildingMap = new Map<string, GroupedByBuilding>();

    for (const item of usage) {
        let building = buildingMap.get(item.buildingKey);
        if (!building) {
            building = { buildingKey: item.buildingKey, buildingName: item.buildingName, levels: [] };
            buildingMap.set(item.buildingKey, building);
        }

        let levelGroup = building.levels.find(l => l.levelDisplay === item.levelDisplay);
        if (!levelGroup) {
            levelGroup = { levelDisplay: item.levelDisplay, rooms: [] };
            building.levels.push(levelGroup);
        }

        let roomGroup = levelGroup.rooms.find(r => r.roomDisplay === item.roomDisplay);
        if (!roomGroup) {
            roomGroup = { roomDisplay: item.roomDisplay, components: [] };
            levelGroup.rooms.push(roomGroup);
        }

        roomGroup.components.push(item);
    }

    const result = Array.from(buildingMap.values());
    result.sort((a, b) => a.buildingName.localeCompare(b.buildingName, 'sv'));

    for (const building of result) {
        building.levels.sort((a, b) => compareNullableFirst(a.levelDisplay, b.levelDisplay));
        for (const level of building.levels) {
            level.rooms.sort((a, b) => compareNullableFirst(a.roomDisplay, b.roomDisplay));
            for (const room of level.rooms) {
                room.components.sort((a, b) => a.componentName.localeCompare(b.componentName, 'sv'));
            }
        }
    }

    return result;
};

export const usageSummary = (instanceCount: number, buildingCount: number): string =>
    `${instanceCount} ${instanceCount === 1 ? 'instans' : 'instanser'} i ${buildingCount} ${buildingCount === 1 ? 'byggnad' : 'byggnader'}`;

/** Link to a component's page, opened in a new tab. */
export const ComponentUsageLink: React.FC<{ item: ComponentUsageInfo }> = ({ item }) => (
    <a
        href={`/building/${item.buildingKey}/component/${item.componentKey}`}
        target="_blank"
        rel="noopener noreferrer"
        className="d-flex align-items-center gap-1 text-decoration-none"
        style={{ color: '#0d6efd' }}
    >
        {item.componentName}
        <ExternalLink size={12} />
    </a>
);

interface ComponentTypeUsageListProps {
    usage: ComponentUsageInfo[];
    /** Max height of the scrolling list; omit to let it flow with the page. */
    maxHeight?: number;
}

/**
 * Every instance of a component type, grouped by building > level > room
 * with a link per component. Shared by the inline "Instanser i byggnader"
 * section on Komponenttyper and the delete modal.
 */
const ComponentTypeUsageList: React.FC<ComponentTypeUsageListProps> = ({ usage, maxHeight }) => {
    const grouped = useMemo(() => groupUsageByBuilding(usage), [usage]);

    if (usage.length === 0) {
        return (
            <p className="text-muted fst-italic mb-0">
                Inga komponenter av denna typ hittades.
            </p>
        );
    }

    return (
        <>
            <div className="component-usage-summary">
                {usageSummary(usage.length, grouped.length)}
            </div>

            <div style={maxHeight ? { maxHeight: `${maxHeight}px`, overflowY: 'auto' } : undefined}>
                {grouped.map((building) => (
                    <div key={building.buildingKey} style={{ marginBottom: '20px' }}>
                        <div style={{ fontWeight: 600, marginBottom: '8px', fontSize: '15px' }}>
                            {building.buildingName}
                        </div>

                        {building.levels.map((level, levelIdx) => (
                            <div key={levelIdx} style={{ marginLeft: level.levelDisplay ? '16px' : '0' }}>
                                {level.levelDisplay && (
                                    <div style={{ fontWeight: 500, fontSize: '14px', marginBottom: '4px', marginTop: '8px', color: '#555' }}>
                                        {level.levelDisplay}
                                    </div>
                                )}

                                {level.rooms.map((room, roomIdx) => (
                                    <div key={roomIdx} style={{ marginLeft: room.roomDisplay ? '16px' : (level.levelDisplay ? '0' : '16px') }}>
                                        {room.roomDisplay && (
                                            <div style={{ fontSize: '13px', marginBottom: '4px', marginTop: '6px', color: '#666' }}>
                                                {room.roomDisplay}
                                            </div>
                                        )}

                                        <ul className="component-usage-items" style={{ marginLeft: room.roomDisplay ? '16px' : '0' }}>
                                            {room.components.map((comp) => (
                                                <li key={comp.componentKey}>
                                                    <ComponentUsageLink item={comp} />
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </>
    );
};

export default ComponentTypeUsageList;
